import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowRight } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { EASE_EXPO, Magnetic } from "@/components/motion/primitives";

const INK = "#0A0F0E";
const BRAND_DARK = "#00332F";
const TEAL = "#2EE6D6";

const LINKS: { title: string; items: { label: string; to: string | { pathname: string; hash: string } }[] }[] = [
  {
    title: "Start",
    items: [
      { label: "Get visa help", to: "/start" },
      { label: "Community", to: "/community" },
      { label: "На русском", to: "/ru" },
    ],
  },
  {
    title: "Learn",
    items: [
      { label: "Country guides", to: "/guides" },
      { label: "Blog", to: "/blog" },
      { label: "FAQ", to: "/faq" },
    ],
  },
  {
    title: "Migrada",
    items: [
      { label: "For universities", to: "/universities" },
      { label: "Contact", to: "/contact" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms of Service", to: "/terms" },
      { label: "Cookies", to: "/cookies" },
      { label: "Impressum", to: "/impressum" },
    ],
  },
];

const SOCIAL = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/migrada",
    path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
  },
  {
    label: "Reddit",
    href: "https://www.reddit.com/r/MoveToEurope/",
    path: "M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z",
  },
];

/**
 * Site footer: one last invitation to get the plan, the links, the legal line,
 * and the name set huge across the bottom, rising into view.
 */
export function FooterRedesigned() {
  return (
    <footer className="relative overflow-hidden" style={{ backgroundColor: INK }}>
      <div className="mx-auto max-w-[1240px] px-5 pt-16 sm:px-8 md:pt-20">
        {/* Last call */}
        <div className="flex flex-col items-start gap-6 border-b pb-12 md:flex-row md:items-end md:justify-between" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, ease: EASE_EXPO }}
            className="max-w-xl font-black leading-[0.98] tracking-[-0.04em] text-white [text-wrap:balance]"
            style={{ fontSize: "clamp(2rem, 1.4rem + 2.4vw, 3.25rem)" }}
          >
            Moving to Austria to study? Start with five questions.
          </motion.p>
          <Magnetic>
            <Link
              to={"/start"}
              className="group inline-flex items-center gap-2 rounded-full px-8 py-4 text-[17px] font-bold transition-[filter,transform] hover:brightness-95 active:scale-95"
              style={{ backgroundColor: TEAL, color: BRAND_DARK }}
            >
              Get visa help
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Magnetic>
        </div>

        {/* Links */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-10 py-12 md:grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))]">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" aria-label="Migrada, home" className="inline-flex">
              <Logo tone="teal" size={34} wordColor="#FFFFFF" />
            </Link>
            <p className="mt-3 max-w-[280px] text-[14.5px] leading-relaxed text-white/60">
              Help for students moving to Austria: a written plan, honest numbers and people who made the move.
            </p>
            <div className="mt-5 flex gap-2">
              {SOCIAL.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/60 transition-all hover:-translate-y-0.5 hover:border-[#2EE6D6] hover:bg-[#2EE6D6] hover:text-[#00332F]"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d={s.path} />
                  </svg>
                </a>
              ))}
            </div>
          </div>
          {LINKS.map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <p className="text-[12px] font-extrabold uppercase tracking-[0.1em]" style={{ color: TEAL }}>
                {col.title}
              </p>
              <ul className="mt-4 space-y-2.5">
                {col.items.map((item) => (
                  <li key={item.label}>
                    <Link to={item.to} className="group relative inline-block text-[15px] text-white/65 transition-colors hover:text-white">
                      {item.label}
                      <span className="absolute -bottom-0.5 left-0 h-[2px] w-full origin-left scale-x-0 transition-transform duration-300 group-hover:scale-x-100" style={{ backgroundColor: TEAL }} aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* Legal */}
        <div className="flex flex-col gap-3 border-t pt-6 text-[12.5px] leading-relaxed text-white/45 md:flex-row md:items-center md:justify-between" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <p className="max-w-2xl">
            Migrada is run by Noah Rodman, BSc, sole trader in Vienna · GISA 40007304 · Data hosted in the EU. Migrada is not a law firm and does not give legal advice.
          </p>
          <p className="flex shrink-0 items-center gap-1.5">
            &copy; {new Date().getFullYear()} Migrada, Vienna
          </p>
        </div>
      </div>

    </footer>
  );
}
